import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Search, Plus, Trash2 } from "lucide-react";

export default function KnowledgeBase() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [opened, setOpened] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Phishing", body: "" });

  const load = async () => {
    const { data } = await api.get(`/knowledge`, { params: { q, category } });
    setItems(data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);
  useEffect(() => { api.get(`/knowledge/categories`).then((r) => setCategories(r.data.items || [])); }, []);

  const open = async (id) => {
    const { data } = await api.get(`/knowledge/${id}`);
    setOpened(data);
  };

  const save = async () => {
    await api.post(`/admin/knowledge`, form);
    setCreating(false); setForm({ title: "", category: "Phishing", body: "" });
    await load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/knowledge/${id}`);
    setOpened(null);
    await load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5" data-testid="knowledge-tab">
      <aside className="csc-card p-4">
        <h3 className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8] mb-2">Categories</h3>
        <button onClick={() => setCategory("")} className={`block w-full text-left px-3 py-1.5 rounded text-sm ${!category ? "bg-[#00aaff]/10 text-[#00aaff]" : "text-[#94a3b8] hover:text-white"}`}>All</button>
        {categories.map((c) => (
          <button key={c} onClick={() => setCategory(c)} className={`block w-full text-left px-3 py-1.5 rounded text-sm ${category === c ? "bg-[#00aaff]/10 text-[#00aaff]" : "text-[#94a3b8] hover:text-white"}`} data-testid={`kb-cat-${c}`}>
            {c}
          </button>
        ))}
      </aside>
      <div className="space-y-3">
        <div className="flex items-center gap-2 csc-card p-3">
          <Search size={14} className="text-[#94a3b8]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search articles…" className="csc-input !py-2 flex-1" data-testid="kb-search" />
          <button onClick={load} className="csc-btn-ghost !py-2" data-testid="kb-search-btn">Search</button>
          {user?.role === "admin" && (
            <button onClick={() => setCreating(true)} className="csc-btn-primary !py-2" data-testid="kb-new-btn">
              <Plus size={12} /> New
            </button>
          )}
        </div>
        {creating && (
          <div className="csc-card p-4 space-y-3" data-testid="kb-create-form">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Article title" className="csc-input" data-testid="kb-create-title" />
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="csc-input" data-testid="kb-create-category">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={8} placeholder="Body (plain text)…" className="csc-input resize-none" data-testid="kb-create-body" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setCreating(false)} className="csc-btn-ghost">Cancel</button>
              <button onClick={save} className="csc-btn-primary" data-testid="kb-create-save">Publish</button>
            </div>
          </div>
        )}
        {opened ? (
          <article className="csc-card p-6" data-testid="kb-article">
            <button onClick={() => setOpened(null)} className="text-xs font-mono-tech uppercase tracking-wider text-[#94a3b8] hover:text-[#00aaff]">← Back to list</button>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#39ff14] mt-3">{opened.category}</div>
            <h2 className="text-2xl font-semibold mt-1">{opened.title}</h2>
            <div className="font-mono-tech text-[11px] text-[#94a3b8] mt-1">by {opened.author_name} · {new Date(opened.created_at).toLocaleString()}</div>
            <div className="mt-4 text-sm whitespace-pre-wrap text-white leading-relaxed">{opened.body}</div>
            {user?.role === "admin" && (
              <button onClick={() => del(opened.id)} className="mt-4 csc-btn-ghost hover:!border-red-400 hover:!text-red-400"><Trash2 size={13} /> Delete</button>
            )}
          </article>
        ) : items.length === 0 ? (
          <div className="csc-card p-8 text-center text-[#94a3b8]" data-testid="kb-empty">No articles in this category yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((a) => (
              <button key={a.id} onClick={() => open(a.id)} className="csc-card p-4 text-left hover:border-[#00aaff]" data-testid={`kb-article-${a.id}`}>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#39ff14]">{a.category}</div>
                <div className="text-sm text-white font-medium mt-1">{a.title}</div>
                <div className="text-xs text-[#94a3b8] mt-2 line-clamp-3">{a.body}</div>
                <div className="font-mono-tech text-[10px] text-[#94a3b8] mt-2">by {a.author_name}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
